/**
 * Login Page - brewauth handoff.
 *
 * brewauth (OIDC) owns the actual sign-in UI now; this page's only job is
 * to kick off the redirect to brewauth (or send an already-authenticated
 * user on to their destination) and show a brief aurora-themed loading
 * state while that happens. The legacy credential/PKI/social form lived
 * here before the brewauth migration.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useAuth } from '@/auth/useAuth';
import { useAppInfo } from '@/app/useAppInfo';
import { login, loginFresh, autoLogin } from '@/auth/reauthClient';
import { SIGN_IN_UNREACHABLE_MESSAGE, useSignInRedirect } from '@/auth/useSignInRedirect';
import AuroraBackground from '@/components/layout/AuroraBackground';
import SessionUnavailablePanel from '@/components/layout/SessionUnavailablePanel';
import { AppFooter, CLASSIFICATION_BANNER_HEIGHT } from '@brew/ui';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authenticated, loading: authLoading, unavailable, localSignout, refreshProfile } = useAuth();
  const [loginBounced, setLoginBounced] = useState(false);
  // A sign-in redirect that could not reach brewauth (#2371).
  const { unreachable, signIn, retry, report } = useSignInRedirect();
  const { info, loading: infoLoading } = useAppInfo();

  const redirectUrl = searchParams.get('redirect') || '/dashboard';

  // If already authenticated (e.g. page reloaded after login or user landed
  // here while still logged in), bounce to the redirect target so we don't
  // trigger a redundant brewauth redirect.
  useEffect(() => {
    if (authenticated) {
      const target = redirectUrl.startsWith('/login') ? '/dashboard' : redirectUrl;
      navigate(target, { replace: true });
    }
  }, [authenticated, navigate, redirectUrl]);

  const license = info?.license;
  const loginDisabled = !infoLoading && !license?.valid;

  // Not authenticated, info settled, license valid: hand off to brewauth.
  // There is no local credential form anymore, so this is the page's only
  // action. Gated like ProtectedRoute (jorigin/brewauth#139): wait for the
  // auth probe, never log in while the backend is unavailable, and count
  // against the same 3-per-120 s cap. Never after a logout that could not
  // reach brewauth: its SSO session may still be live (#2192).
  useEffect(() => {
    if (!authLoading && !authenticated && !unavailable && !localSignout && !infoLoading && !loginDisabled) {
      if (!autoLogin(() => report(login))) queueMicrotask(() => setLoginBounced(true));
    }
  }, [authLoading, authenticated, unavailable, localSignout, infoLoading, loginDisabled, report]);

  const renderPanel = () => {
    if (loginDisabled) {
      return (
        <p className="ic-required text-sm">
          {license ? license.reason : 'License missing or invalid'}
        </p>
      );
    }
    if (!authLoading && !authenticated && unreachable) {
      return (
        <SessionUnavailablePanel
          message={SIGN_IN_UNREACHABLE_MESSAGE}
          actionLabel="Retry"
          onAction={retry}
        />
      );
    }
    if (!authLoading && localSignout) {
      return (
        <SessionUnavailablePanel
          message="You're signed out."
          actionLabel="Sign in"
          onAction={() => signIn(loginFresh)}
        />
      );
    }
    if (!authLoading && !authenticated && unavailable) {
      return (
        <SessionUnavailablePanel
          message="We can't reach brewlytics right now."
          actionLabel="Retry"
          onAction={() => void refreshProfile()}
        />
      );
    }
    if (!authLoading && !authenticated && loginBounced) {
      return (
        <SessionUnavailablePanel
          message="Sign-in keeps bouncing back. Your work is safe."
          actionLabel="Sign in"
          onAction={() => signIn(login)}
        />
      );
    }
    return (
      <p className="text-sm text-brew-blue-light/65">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Redirecting to sign in…
      </p>
    );
  };

  return (
    <>
      <AuroraBackground />

      <div className="min-h-[var(--app-content-height)] flex items-center justify-center px-4 py-12 pb-24">
        <div className="w-full max-w-md text-center">
          {renderPanel()}
        </div>
      </div>
      <AppFooter
        closeIcon={<FontAwesomeIcon icon={faXmark} />}
        bottomOffset={info?.capco?.enabled ? CLASSIFICATION_BANNER_HEIGHT : 0}
      />
    </>
  );
}
